import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtPrice } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { useLang } from "../context/LanguageContext";

const TIMES = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM", "6:00 PM"];

const EMPTY = { name: "", phone: "", email: "", date: "", time: "10:30 AM", notes: "" };

const inputCls =
  "w-full border border-engraved bg-paper px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-navy";

const BookingModal = () => {
  const { open, listing, service, closeBooking } = useBooking();
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [serviceType, setServiceType] = useState(service);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setServiceType(service || "tour");
      setForm(EMPTY);
    }
  }, [open, service]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const services = [
    { value: "tour", label: t("bk.tour") },
    { value: "qualification", label: t("bk.qualification") },
    { value: "credit_repair", label: t("bk.credit_repair") },
    { value: "down_payment", label: t("bk.down_payment") },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/appointments", {
        ...form,
        email: form.email || null,
        notes: form.notes || null,
        service_type: serviceType,
        listing_id: listing?.id || null,
        listing_label: listing ? `${listing.title} · ${fmtPrice(listing.price)}` : null,
      });
      toast.success(t("bk.success"));
      closeBooking();
    } catch (err) {
      toast.error(t("bk.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-navy/60 p-4 backdrop-blur-sm"
          onClick={closeBooking}
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="engraved-frame relative my-8 w-full max-w-lg bg-paper p-8 shadow-lifted"
          >
            <button
              data-testid="booking-modal-close-button"
              onClick={closeBooking}
              className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-crimson"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <p className="eyebrow">{t("bk.eyebrow")}</p>
            <h3 className="mt-2 font-serif text-2xl font-bold text-navy">
              {t("bk.title")}
            </h3>

            {listing && (
              <div className="mt-4 flex items-center justify-between border border-engraved bg-white px-4 py-3">
                <span data-testid="booking-listing-label" className="text-sm font-medium text-ink">{listing.title}</span>
                <span className="font-mono text-xs text-crimson">{fmtPrice(listing.price)}</span>
              </div>
            )}

            <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input data-testid="booking-name-input" required placeholder={t("bk.namePh")} value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="booking-phone-input" required placeholder={t("bk.phonePh")} type="tel" value={form.phone} onChange={set("phone")} className={inputCls} />
              <input data-testid="booking-email-input" placeholder={t("bk.emailPh")} type="email" value={form.email} onChange={set("email")} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="booking-date-input" required type="date" value={form.date} onChange={set("date")} className={inputCls} />
              <select data-testid="booking-time-select" value={form.time} onChange={set("time")} className={inputCls}>
                {TIMES.map((tm) => <option key={tm}>{tm}</option>)}
              </select>
              <select data-testid="booking-service-select" value={serviceType} onChange={(e) => setServiceType(e.target.value)} className={`${inputCls} sm:col-span-2`}>
                {services.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <textarea data-testid="booking-notes-input" placeholder={t("bk.notesPh")} rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} sm:col-span-2`} />
              <button
                data-testid="booking-modal-submit-button"
                disabled={sending}
                className="bg-navy px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-crimson disabled:opacity-60 sm:col-span-2"
              >
                {sending ? t("bk.sending") : t("bk.submit")}
              </button>
            </form>

            <p className="mt-4 text-center font-mono text-[11px] text-slate-500">
              {t("bk.prefer")} <a href="tel:+17022414761" className="text-crimson">702·241·4761</a>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
